import React, { useState, useRef } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import PageHero from '../components/PageHero'

const INITIAL_FORM = {
  // Personal
  fullName: '',
  email: '',
  phone: '',
  gender: '',
  ageRange: '',
  location: '',
  // Professional
  schoolName: '',
  schoolLocation: '',
  role: '',
  experience: '',
  // Interest
  motivation: '',
  hasActivityExperience: '',
  activityExperience: '',
  // School info
  studentCount: '',
  schoolType: '',
  schoolHasFootball: '',
  // Head of school
  headName: '',
  headRole: '',
  headEmail: '',
  headPhone: '',
  headAwareness: '',
  // Final
  agree: false,
}

const WHY_CARDS = [
  {
    icon: 'bi-dribbble',
    title: 'Football Development',
    desc: 'Support structured football activities that help students develop skills, discipline, confidence, and teamwork.',
  },
  {
    icon: 'bi-mortarboard-fill',
    title: 'Education & Growth',
    desc: 'Help reinforce the connection between learning, personal development, and sports.',
  },
  {
    icon: 'bi-people-fill',
    title: 'Community Impact',
    desc: 'Become part of a movement creating positive opportunities for children within schools.',
  },
]

const ROLE_EXPECTATIONS = [
  'Support SISDI activities within your school',
  'Encourage student participation',
  'Coordinate communication between SISDI and school leadership',
  'Promote discipline, teamwork, and leadership values',
  'Help identify student potential',
  'Provide feedback on activities and progress',
]

const BENEFITS = [
  {
    icon: 'bi-patch-check-fill',
    title: 'Official SISDI Recognition',
    desc: 'Receive a SISDI Coordinator certificate and official recognition as a trusted partner in youth development.',
  },
  {
    icon: 'bi-mortarboard-fill',
    title: 'Training & Capacity Building',
    desc: 'Access coaching clinics, mentorship workshops, and child-development training to grow your professional skills.',
  },
  {
    icon: 'bi-diagram-3-fill',
    title: 'A National Network',
    desc: 'Join a community of like-minded educators and coaches across schools, sharing ideas, support, and opportunities.',
  },
  {
    icon: 'bi-trophy-fill',
    title: 'Programs & Events Access',
    desc: 'Bring structured football, tournaments, and SISDI activities directly to your students at no cost to your school.',
  },
  {
    icon: 'bi-stars',
    title: 'Visibility & Leadership',
    desc: 'Be spotlighted across SISDI platforms and take on a leadership role that strengthens your standing in your school.',
  },
  {
    icon: 'bi-heart-fill',
    title: 'Real, Lasting Impact',
    desc: 'Directly shape the discipline, confidence, and futures of the students you mentor — work that truly matters.',
  },
]

export default function Apply() {
  const apiBase = process.env.REACT_APP_API_BASE || ''
  const [formData, setFormData] = useState(INITIAL_FORM)
  const [status, setStatus] = useState('idle') // idle | loading | success | error
  const [alert, setAlert] = useState(null)
  const formRef = useRef(null)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (status === 'loading') return
    setStatus('loading')
    setAlert(null)

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 20000)

    try {
      const base = apiBase ? apiBase.replace(/\/$/, '') : ''
      const response = await fetch(`${base}/api/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        signal: controller.signal,
      })

      const data = await response.json()
      if (!response.ok || !data.success) {
        throw new Error(data?.errors?.[0]?.msg || data?.error || 'Failed to submit application')
      }

      setStatus('success')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      setStatus('error')
      const message =
        err.name === 'AbortError'
          ? 'The request timed out. Please try again in a moment.'
          : err.message || 'Failed to submit application. Please try again.'
      setAlert({ type: 'danger', message })
    } finally {
      clearTimeout(timeoutId)
    }
  }

  /* ---------- Success state ---------- */
  if (status === 'success') {
    return (
      <>
        <Navbar />
        <main>
          <section className="section apply-success-section">
            <div className="container">
              <div className="apply-success-card fade-in">
                <div className="apply-success-icon" aria-hidden="true">
                  <i className="bi bi-check-lg"></i>
                </div>
                <span className="eyebrow">Application Received</span>
                <h2 style={{ marginTop: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                  Thank you for applying to become a{' '}
                  <span className="text-accent">SISDI School Coordinator</span>
                </h2>
                <p className="lead" style={{ marginInline: 'auto' }}>
                  Your application has been received. Our team will review your information and
                  contact you with the next steps.
                </p>
                <div style={{ marginTop: 'var(--space-6)' }}>
                  <a href="/" className="btn btn-primary btn-lg">
                    <i className="bi bi-house-door-fill"></i> Return to SISDI Homepage
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </>
    )
  }

  return (
    <>
      <Navbar />

      <PageHero
        title="Become a SISDI School Coordinator"
        subtitle="Join a growing network of educators helping build futures through football and education."
        variant="involved"
        fadeClass="fade-in"
      />

      <main>
        {/* Intro + CTA */}
        <section className="section fade-in">
          <div className="container" style={{ textAlign: 'center' }}>
            <p className="section-lead">
              Are you a teacher passionate about youth development, football, and education? Apply to
              become a SISDI School Coordinator and help create meaningful opportunities for students
              within your school.
            </p>
            <div style={{ marginTop: 'var(--space-6)' }}>
              <button type="button" className="btn btn-primary btn-lg" onClick={scrollToForm}>
                <i className="bi bi-pencil-square"></i> Start Application
              </button>
            </div>
          </div>
        </section>

        {/* Why join */}
        <section className="section bg-light fade-in">
          <div className="container">
            <div className="section-header">
              <span className="eyebrow">The Opportunity</span>
              <h2>
                Why become a <span className="text-accent">SISDI School Coordinator?</span>
              </h2>
              <span className="section-divider"></span>
            </div>
            <div className="grid-3 fade-in fade-in-stagger">
              {WHY_CARDS.map((card, i) => (
                <div className="involve-card" key={i}>
                  <div className="icon-circle">
                    <i className={`bi ${card.icon}`}></i>
                  </div>
                  <h4>{card.title}</h4>
                  <p>{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Role expectations */}
        <section className="section fade-in">
          <div className="container">
            <div className="grid-2" style={{ alignItems: 'center' }}>
              <div>
                <span className="eyebrow">The Role</span>
                <h2 style={{ marginTop: 'var(--space-1)', marginBottom: 'var(--space-3)' }}>
                  Your role as a <span className="text-accent">SISDI School Coordinator</span>
                </h2>
                <p>
                  As a coordinator, you become the bridge between SISDI and your students — helping
                  bring structured football and education programs to life within your school.
                </p>
              </div>
              <ul className="apply-role-list fade-in fade-in-stagger">
                {ROLE_EXPECTATIONS.map((item, i) => (
                  <li key={i}>
                    <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="section bg-light fade-in">
          <div className="container">
            <div className="section-header">
              <span className="eyebrow">What You Gain</span>
              <h2>
                Benefits of being a <span className="text-accent">SISDI Coordinator</span>
              </h2>
              <span className="section-divider"></span>
              <p>
                This is more than a role — it&apos;s an investment in you, your students, and your
                school. Here&apos;s what you stand to gain.
              </p>
            </div>
            <div className="grid-3 fade-in fade-in-stagger">
              {BENEFITS.map((b, i) => (
                <div className="involve-card" key={i}>
                  <div className="icon-circle">
                    <i className={`bi ${b.icon}`}></i>
                  </div>
                  <h4>{b.title}</h4>
                  <p>{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application form */}
        <section className="section bg-light fade-in" ref={formRef}>
          <div className="container apply-form-container">
            <div className="section-header">
              <span className="eyebrow">Application</span>
              <h2>
                Apply to <span className="text-accent">Become a Coordinator</span>
              </h2>
              <p>
                Please complete all sections below. Fields marked with an asterisk (*) are required.
              </p>
            </div>

            {alert && (
              <div className={`alert alert-${alert.type}`} role="alert">
                {alert.message}
              </div>
            )}

            <form className="contact-form apply-form" onSubmit={handleSubmit} noValidate>
              {/* Personal information */}
              <fieldset className="apply-fieldset">
                <legend className="apply-legend">
                  <span className="apply-legend-num">1</span> Personal Information
                </legend>
                <div className="apply-grid-2">
                  <div className="apply-field">
                    <label htmlFor="fullName">Full Name *</label>
                    <input id="fullName" name="fullName" type="text" className="form-control"
                      placeholder="Your full name" required value={formData.fullName} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="email">Email Address *</label>
                    <input id="email" name="email" type="email" className="form-control"
                      placeholder="your@email.com" required value={formData.email} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="phone">Phone Number *</label>
                    <input id="phone" name="phone" type="tel" className="form-control"
                      placeholder="+234..." required value={formData.phone} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="gender">Gender *</label>
                    <select id="gender" name="gender" className="form-control" required
                      value={formData.gender} onChange={handleChange}>
                      <option value="" disabled>Select…</option>
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>
                  <div className="apply-field">
                    <label htmlFor="ageRange">Age Range *</label>
                    <select id="ageRange" name="ageRange" className="form-control" required
                      value={formData.ageRange} onChange={handleChange}>
                      <option value="" disabled>Select…</option>
                      <option value="Under 25">Under 25</option>
                      <option value="25–34">25–34</option>
                      <option value="35–44">35–44</option>
                      <option value="45–54">45–54</option>
                      <option value="55+">55+</option>
                    </select>
                  </div>
                  <div className="apply-field">
                    <label htmlFor="location">Location (State, Country) *</label>
                    <input id="location" name="location" type="text" className="form-control"
                      placeholder="e.g. Lagos, Nigeria" required value={formData.location} onChange={handleChange} />
                  </div>
                </div>
              </fieldset>

              {/* Professional information */}
              <fieldset className="apply-fieldset">
                <legend className="apply-legend">
                  <span className="apply-legend-num">2</span> Professional Information
                </legend>
                <div className="apply-grid-2">
                  <div className="apply-field">
                    <label htmlFor="schoolName">School Name *</label>
                    <input id="schoolName" name="schoolName" type="text" className="form-control"
                      placeholder="Name of your school" required value={formData.schoolName} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="schoolLocation">School Location *</label>
                    <input id="schoolLocation" name="schoolLocation" type="text" className="form-control"
                      placeholder="City / town" required value={formData.schoolLocation} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="role">Your Role / Position *</label>
                    <input id="role" name="role" type="text" className="form-control"
                      placeholder="e.g. Sports Teacher" required value={formData.role} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="experience">Years of Teaching Experience *</label>
                    <select id="experience" name="experience" className="form-control" required
                      value={formData.experience} onChange={handleChange}>
                      <option value="" disabled>Select…</option>
                      <option value="Less than 1 year">Less than 1 year</option>
                      <option value="1–3 years">1–3 years</option>
                      <option value="4–7 years">4–7 years</option>
                      <option value="8–15 years">8–15 years</option>
                      <option value="15+ years">15+ years</option>
                    </select>
                  </div>
                </div>
              </fieldset>

              {/* Football & education interest */}
              <fieldset className="apply-fieldset">
                <legend className="apply-legend">
                  <span className="apply-legend-num">3</span> Football &amp; Education Interest
                </legend>

                <div className="apply-field apply-field-full">
                  <label htmlFor="motivation">
                    Why are you interested in becoming a SISDI School Coordinator? *
                  </label>
                  <textarea id="motivation" name="motivation" className="form-control" rows="4"
                    placeholder="Share what motivates you…" required
                    value={formData.motivation} onChange={handleChange}></textarea>
                </div>

                <div className="apply-field apply-field-full">
                  <span className="apply-question-label">
                    Do you have experience supporting football, sports, student development, or
                    extracurricular activities? *
                  </span>
                  <div className="apply-radio-group" role="radiogroup">
                    {['Yes', 'No'].map((opt) => (
                      <label className="apply-radio" key={opt}>
                        <input type="radio" name="hasActivityExperience" value={opt}
                          checked={formData.hasActivityExperience === opt} onChange={handleChange} required />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {formData.hasActivityExperience === 'Yes' && (
                  <div className="apply-field apply-field-full apply-reveal">
                    <label htmlFor="activityExperience">Describe your experience. *</label>
                    <textarea id="activityExperience" name="activityExperience" className="form-control" rows="3"
                      placeholder="Tell us about your relevant experience…" required
                      value={formData.activityExperience} onChange={handleChange}></textarea>
                  </div>
                )}
              </fieldset>

              {/* School information */}
              <fieldset className="apply-fieldset">
                <legend className="apply-legend">
                  <span className="apply-legend-num">4</span> School Information
                </legend>
                <div className="apply-grid-2">
                  <div className="apply-field">
                    <label htmlFor="studentCount">Number of students in your school *</label>
                    <input id="studentCount" name="studentCount" type="number" min="0" className="form-control"
                      placeholder="e.g. 450" required value={formData.studentCount} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="schoolType">School Type *</label>
                    <select id="schoolType" name="schoolType" className="form-control" required
                      value={formData.schoolType} onChange={handleChange}>
                      <option value="" disabled>Select…</option>
                      <option value="Primary">Primary</option>
                      <option value="Secondary">Secondary</option>
                      <option value="Combined">Combined</option>
                    </select>
                  </div>
                </div>

                <div className="apply-field apply-field-full">
                  <span className="apply-question-label">
                    Does your school currently have football activities? *
                  </span>
                  <div className="apply-radio-group" role="radiogroup">
                    {['Yes', 'No'].map((opt) => (
                      <label className="apply-radio" key={opt}>
                        <input type="radio" name="schoolHasFootball" value={opt}
                          checked={formData.schoolHasFootball === opt} onChange={handleChange} required />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </fieldset>

              {/* Head of school */}
              <fieldset className="apply-fieldset">
                <legend className="apply-legend">
                  <span className="apply-legend-num">5</span> Head of School / School Management
                </legend>
                <p className="apply-fieldset-note">
                  We reach out to your school&apos;s leadership to confirm your application and
                  introduce our programme. Please share their details so we can connect.
                </p>
                <div className="apply-grid-2">
                  <div className="apply-field">
                    <label htmlFor="headName">Head of School&apos;s Full Name *</label>
                    <input id="headName" name="headName" type="text" className="form-control"
                      placeholder="Name of principal / head teacher" required
                      value={formData.headName} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="headRole">Their Role / Title</label>
                    <input id="headRole" name="headRole" type="text" className="form-control"
                      placeholder="e.g. Principal, Head Teacher"
                      value={formData.headRole} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="headEmail">Their Email Address</label>
                    <input id="headEmail" name="headEmail" type="email" className="form-control"
                      placeholder="head@school.com"
                      value={formData.headEmail} onChange={handleChange} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="headPhone">Their Phone Number *</label>
                    <input id="headPhone" name="headPhone" type="tel" className="form-control"
                      placeholder="+234..." required
                      value={formData.headPhone} onChange={handleChange} />
                  </div>
                </div>

                <div className="apply-field apply-field-full">
                  <span className="apply-question-label">
                    Is your head of school / management already aware of SISDI? *
                  </span>
                  <div className="apply-radio-group apply-radio-group-stacked" role="radiogroup">
                    {[
                      { value: 'Aware and supportive', label: 'Yes — they are already aware of SISDI and supportive' },
                      { value: 'Aware not yet discussed', label: 'They are aware, but we have not yet discussed running the programme' },
                      { value: 'Please introduce', label: 'No — I’d like SISDI to speak with them and introduce the programme' },
                    ].map((opt) => (
                      <label className="apply-radio" key={opt.value}>
                        <input type="radio" name="headAwareness" value={opt.value}
                          checked={formData.headAwareness === opt.value} onChange={handleChange} required />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </fieldset>

              {/* Final */}
              <div className="apply-agree">
                <label className="apply-checkbox">
                  <input type="checkbox" name="agree" checked={formData.agree} onChange={handleChange} required />
                  <span>
                    I agree to support SISDI’s mission of building futures through football and
                    education.
                  </span>
                </label>
              </div>

              <button type="submit" className="btn btn-primary btn-lg apply-submit"
                disabled={status === 'loading'}>
                {status === 'loading' ? (
                  <><i className="bi bi-arrow-repeat apply-spin"></i> Submitting…</>
                ) : (
                  <><i className="bi bi-send-fill"></i> Submit Application</>
                )}
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}
